import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import Button from './Button';

const SearchComponent = ({ 
  placeholder = "Search...", 
  onSearch, 
  onClear,
  searchScope = "all",
  className = "",
  expandable = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(!expandable);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const searchScopes = {
    all: { label: 'All', icon: 'Search' },
    problems: { label: 'Problems', icon: 'FileText' },
    teams: { label: 'Teams', icon: 'Users' },
    users: { label: 'Users', icon: 'User' },
    learning: { label: 'Learning', icon: 'BookOpen' }
  };

  useEffect(() => {
    if (isExpanded && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    if (searchTimeoutRef?.current) {
      clearTimeout(searchTimeoutRef?.current);
    }

    if (searchValue?.trim()) {
      setIsLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        if (onSearch) {
          onSearch(searchValue?.trim(), searchScope);
        }
        setIsLoading(false);
      }, 300);
    } else {
      setIsLoading(false);
      if (onClear) {
        onClear();
      }
    }

    return () => {
      if (searchTimeoutRef?.current) {
        clearTimeout(searchTimeoutRef?.current);
      }
    };
  }, [searchValue, searchScope, onSearch, onClear]);

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    setSearchValue('');
    if (onClear) {
      onClear();
    }
  };

  const handleClear = () => {
    setSearchValue('');
    if (onClear) {
      onClear();
    }
    if (inputRef?.current) {
      inputRef?.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') {
      if (expandable) {
        handleCollapse();
      } else {
        handleClear();
      }
    }
  };

  if (expandable && !isExpanded) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleExpand}
        className={`transition-micro ${className}`}
      >
        <Icon name="Search" size={20} />
      </Button>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
          {isLoading ? (
            <Icon name="Loader2" size={16} className="animate-spin" />
          ) : (
            <Icon name={searchScopes?.[searchScope]?.icon || 'Search'} size={16} />
          )}
        </div>
        
        <Input
          ref={inputRef}
          type="search"
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => setSearchValue(e?.target?.value)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-10"
        />

        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {searchValue && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="h-6 w-6 transition-micro"
            >
              <Icon name="X" size={14} />
            </Button>
          )}
          
          {expandable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCollapse}
              className="h-6 w-6 transition-micro"
            >
              <Icon name="ChevronUp" size={14} />
            </Button>
          )}
        </div>
      </div>
      {/* Search Scope Indicator */}
      {searchScope !== 'all' && (
        <div className="absolute -bottom-6 left-0">
          <span className="inline-flex items-center space-x-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
            <Icon name={searchScopes?.[searchScope]?.icon} size={12} />
            <span>Searching in {searchScopes?.[searchScope]?.label}</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;